import React, { useState, useEffect } from 'react';
import './news.css';
import { FaCalendarAlt, FaMapMarkerAlt, FaRegClock, FaSearch, FaShare, FaBookmark, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import NewsCard from './NewsCard';
import EventCard from './EventCard';

const News = () => {
  const [activeTab, setActiveTab] = useState('news');
  const [newsData, setNewsData] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);

  // Sample data - can be moved to a separate file if needed
  const sampleNewsData = [
    {
      id: 1,
      title: 'Asian Polytechnic Campus Launches New AI Research Center',
      date: '2023-05-15',
      excerpt: 'The campus has established a state-of-the-art AI research facility with industry partnerships.',
      image: '/images/news/ai-center.jpg',
      category: 'Research'
    },
    {
      id: 2,
      title: 'Annual Tech Symposium Attracts Over 500 Participants',
      date: '2023-04-28',
      excerpt: 'Students and professionals gathered for the largest technology conference in the region.',
      image: '/images/news/tech-symposium.jpg',
      category: 'Event'
    },
    {
      id: 3,
      title: 'Campus Ranked Among Top 10 Engineering Schools in Asia',
      date: '2023-03-10',
      excerpt: 'Latest rankings place Asian Polytechnic in the elite category for engineering education.',
      image: '/images/news/ranking.jpg',
      category: 'Achievement'
    }
  ];

  const sampleEventsData = [
    {
      id: 1,
      title: 'Open House 2023',
      date: '2023-06-15',
      time: '09:00 - 16:00',
      location: 'Main Campus Quad',
      description: 'Prospective students can tour our facilities and meet faculty members.',
      image: '/images/events/open-house.jpg'
    },
    {
      id: 2,
      title: 'Industry Leaders Forum',
      date: '2023-06-22',
      time: '14:00 - 17:00',
      location: 'Auditorium A',
      description: 'Panel discussion with CEOs from leading tech companies in the region.',
      image: '/images/events/industry-forum.jpg'
    },
    {
      id: 3,
      title: 'Hackathon 2023',
      date: '2023-07-05',
      time: '08:00 - 20:00',
      location: 'Innovation Lab',
      description: '48-hour coding competition with prizes for winning teams.',
      image: '/images/events/hackathon.jpg'
    }
  ];

  useEffect(() => {
    // Load sample data directly
    setNewsData(sampleNewsData);
    setEventsData(sampleEventsData);
  }, []);

  const filteredNews = newsData.filter(news => 
    news.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    news.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredEvents = eventsData.filter(event => 
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === filteredEvents.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? filteredEvents.length - 1 : prev - 1));
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const email = e.target.elements.email.value;
    alert(`Thank you for subscribing with ${email}! You'll receive our updates soon.`);
    e.target.reset();
  };

  return (
    <div className="news-events-container">
      <div className="hero-banner">
        <h1>Campus News & Events</h1>
        <p>Stay updated with the latest happenings at Asian Polytechnic Campus</p>
      </div>

      <div className="content-wrapper">
        <div className="search-filter-section">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input 
              type="text" 
              placeholder="Search news and events..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="tab-buttons">
            <button 
              className={activeTab === 'news' ? 'active' : ''}
              onClick={() => setActiveTab('news')}
            >
              Latest News
            </button>
            <button 
              className={activeTab === 'events' ? 'active' : ''}
              onClick={() => setActiveTab('events')}
            >
              Upcoming Events
            </button>
          </div>
        </div>

        {activeTab === 'news' ? (
          <div className="news-section">
            <h2 className="section-title">Campus News</h2>
            {filteredNews.length === 0 ? (
              <p className="no-results">No news articles found matching your search.</p>
            ) : (
              <div className="news-grid">
                {filteredNews.map(news => (
                  <NewsCard 
                    key={news.id}
                    title={news.title}
                    date={news.date}
                    excerpt={news.excerpt}
                    image={news.image}
                    category={news.category}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="events-section">
            <h2 className="section-title">Upcoming Events</h2>
            {filteredEvents.length === 0 ? (
              <p className="no-results">No upcoming events found matching your search.</p>
            ) : (
              <>
                <div className="events-carousel">
                  <button className="carousel-button prev" onClick={prevSlide}>
                    <FaChevronLeft />
                  </button>
                  
                  <div className="carousel-track">
                    {filteredEvents.map((event, index) => (
                      <div 
                        className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
                        key={event.id}
                      >
                        <EventCard 
                          title={event.title}
                          date={event.date}
                          time={event.time}
                          location={event.location}
                          description={event.description}
                          image={event.image}
                        />
                      </div>
                    ))}
                  </div>
                  
                  <button className="carousel-button next" onClick={nextSlide}>
                    <FaChevronRight />
                  </button>
                </div>
                
                <div className="events-grid">
                  {filteredEvents.map(event => (
                    <EventCard 
                      key={event.id}
                      title={event.title}
                      date={event.date}
                      time={event.time}
                      location={event.location}
                      description={event.description}
                      image={event.image}
                      variant="grid"
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="newsletter-section">
          <h3>Subscribe to Our Newsletter</h3>
          <p>Get the latest news and event updates directly to your inbox</p>
          <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
            <input 
              type="email" 
              name="email"
              placeholder="Your email address" 
              required 
            />
            <button type="submit">Subscribe</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default News;